import React, { useState } from 'react';
import { FindAllMyProjectsResponse } from '@ansvar-sakerhet/api-client';
import { S } from './ProjectsTable.styles';
import { useTable } from 'react-table';
import { useMyProjectsTableData } from './hooks/useMyProjectsTableData';
import { SortControls } from '../../../../../../traits/api/projects/useGetMyProjects';
import { Pagination } from '../../../../../../components/Pagination';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../../../traits/hooks/useAuth';
import { ProjectRowTarget } from './components/ActionsCell';
import { RenameProjectDialog } from './components/RenameProjectDialog';
import { DeleteProjectDialog } from './components/DeleteProjectDialog';

export type Props = {
  projects: FindAllMyProjectsResponse[];
  setSort: (sort: SortControls) => void;
  sort: SortControls;
  setPage: (page: number) => void;
  page: number;
  numberOfPages: number;
};

export const ProjectsTable = ({
  projects,
  setSort,
  sort,
  setPage,
  page,
  numberOfPages,
}: Props) => {
  const { isCustomerAdmin } = useAuth();
  const canManageProject = isCustomerAdmin;
  const [renameTarget, setRenameTarget] = useState<ProjectRowTarget | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] = useState<ProjectRowTarget | null>(
    null
  );

  const data = useMyProjectsTableData(
    projects,
    canManageProject,
    setRenameTarget,
    setDeleteTarget
  );
  const table = useTable(data);
  const navigate = useNavigate();

  return (
    <S.TableContainer>
      <S.AltTable {...table.getTableProps()}>
        <S.AltTHead>
          {table.headerGroups.map((headerGroup) => (
            <S.AltTHeadRow
              {...headerGroup.getHeaderGroupProps()}
              key={headerGroup.getHeaderGroupProps().key}
            >
              {headerGroup.headers.map((column) => {
                const headerData = data.columns.filter(
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ({ accessor }: any) => accessor === column.id
                )[0];
                const interactiveProps = headerData?.onClick
                  ? { onClick: () => headerData?.onClick(setSort, sort) }
                  : {};
                const iconSrc = headerData?.iconSrc?.(sort);

                return (
                  <S.AltTHeadCell
                    {...column.getHeaderProps()}
                    {...interactiveProps}
                    key={column.getHeaderProps().key}
                  >
                    <S.TableText>{column.render('Header')}</S.TableText>
                    {iconSrc && <S.CenteredIcon src={iconSrc} />}
                  </S.AltTHeadCell>
                );
              })}
            </S.AltTHeadRow>
          ))}
        </S.AltTHead>

        <S.AltTBody {...table.getTableBodyProps()}>
          {table.rows.map((row) => {
            table.prepareRow(row);

            return (
              <S.AltTBodyRow {...row.getRowProps()} key={row.getRowProps().key}>
                {row.cells.map((cell) => {
                  const projectId = cell.row.original.projectId;

                  return (
                    <S.AltTBodyCell
                      onClick={() => navigate(`/mina-projekt/${projectId}`)}
                      {...cell.getCellProps()}
                      key={cell.getCellProps().key}
                    >
                      {cell.render('Cell')}
                    </S.AltTBodyCell>
                  );
                })}
              </S.AltTBodyRow>
            );
          })}
        </S.AltTBody>
      </S.AltTable>
      <S.PaginationContainer>
        <Pagination
          page={page}
          setPage={setPage}
          numberOfPages={numberOfPages}
        />
      </S.PaginationContainer>
      <RenameProjectDialog
        target={renameTarget}
        onClose={() => setRenameTarget(null)}
      />
      <DeleteProjectDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
    </S.TableContainer>
  );
};
